import {
  deletePermissionMetadata,
  getExpiredGroupFeedsPermissions,
  getDataFeedMetadata,
  getDataFeedsApprovalService,
  getGroupFeedsPermissions,
  getPermissionApprovals,
  getPermissionMetadata,
  getServiceDataFeeds,
  putPermissionApproval,
  putPermissionMetadata,
  putGroupFeedsPermissions,
  updatePermissionApproval,
} from '../../services';

import { EntitlementsModel } from './entitlements-model';
import { BaseResourceController } from '../shared/base-resource-controller';

import type {
  EntitlementsParams,
  ResourceKey,
  FeedEntitlementRow,
  ApprovalsByEntitlement,
  DataFeedMetadataByFeedId,
  EntitlementsMetadataByEntitlement,
  InventoryFeedRow,
} from './entitlements-types';

import { ENTITLEMENTS_RESOURCE_KEYS } from './entitlements-constants';

type DataFeedMetadataRow = Record<string, unknown> & {
  feedId: string;
};

type SaveChangesArgs = {
  feedDifference: Record<string, unknown>;
  approvals?: Record<string, unknown>;
};

export class EntitlementsController extends BaseResourceController<ResourceKey, EntitlementsModel> {
  private readonly entitlementsModel: EntitlementsModel;
  private readonly data: EntitlementsParams;

  constructor(model: EntitlementsModel) {
    super(ENTITLEMENTS_RESOURCE_KEYS);
    this.entitlementsModel = model;
    this.data = model.data;
  }

  protected getModel = () => this.entitlementsModel;

  public fetchEntitlements = async () => {
    const key: ResourceKey = 'entitlements';
    await this.withRequest(key, {
      run: async ({ shouldIgnore }) => {
        const response = await getGroupFeedsPermissions(this.data.permissionId);
        if (shouldIgnore()) {
          return;
        }

        const entitlements =
          (response as { groupDataFeedPermissions?: FeedEntitlementRow[] } | null | undefined)?.groupDataFeedPermissions ?? [];
        this.entitlementsModel.setEntitlements(entitlements);
      },
    });
  };

  public fetchExpiredEntitlements = async () => {
    const key: ResourceKey = 'expiredEntitlements';
    await this.withRequest(key, {
      run: async ({ shouldIgnore }) => {
        const response = await getExpiredGroupFeedsPermissions(this.data.permissionId);
        if (shouldIgnore()) {
          return;
        }

        const entitlements =
          (response as { groupDataFeedPermissions?: FeedEntitlementRow[] } | null | undefined)?.groupDataFeedPermissions ?? [];
        this.entitlementsModel.setExpiredEntitlements(entitlements);
      },
    });
  };

  public fetchApprovals = async () => {
    const key: ResourceKey = 'approvals';
    await this.withRequest(key, {
      run: async ({ shouldIgnore }) => {
        const response = await getPermissionApprovals(this.data.permissionId);
        if (shouldIgnore()) {
          return;
        }

        this.entitlementsModel.setApprovals((response ?? {}) as ApprovalsByEntitlement);
      },
    });
  };

  public fetchEntitlementsMetadata = async () => {
    const key: ResourceKey = 'entitlementsMetadata';
    await this.withRequest(key, {
      run: async ({ shouldIgnore }) => {
        const response = await getPermissionMetadata(this.data.permissionId);
        if (shouldIgnore()) {
          return;
        }

        this.entitlementsModel.setEntitlementsMetadata((response ?? {}) as EntitlementsMetadataByEntitlement);
      },
    });
  };

  public fetchDataFeedContext = async () => {
    const key: ResourceKey = 'dataFeedContext';
    await this.withRequest(key, {
      run: async ({ shouldIgnore }) => {
        const [dataFeedInventory, feedIdsRequiringApproval, dataFeedMetadata] = await Promise.all([
          getServiceDataFeeds(String(this.data.serviceId)),
          getDataFeedsApprovalService(String(this.data.serviceId)),
          getDataFeedMetadata(this.data.serviceId),
        ]);

        if (shouldIgnore()) {
          return;
        }

        const metadataByFeedId = ((dataFeedMetadata ?? []) as DataFeedMetadataRow[]).reduce((acc: DataFeedMetadataByFeedId, metadata) => {
          acc[metadata.feedId] = metadata;
          return acc;
        }, {} as DataFeedMetadataByFeedId);

        this.entitlementsModel.setDataFeedContext({
          feedInventory: (dataFeedInventory ?? []) as InventoryFeedRow[],
          feedIdsRequiringApproval: (feedIdsRequiringApproval ?? []) as Array<string>,
          dataFeedMetadata: metadataByFeedId,
        });
      },
    });
  };

  public refreshAll = async () => {
    await Promise.allSettled([
      this.fetchDataFeedContext(),
      this.fetchEntitlements(),
      this.fetchExpiredEntitlements(),
      this.fetchApprovals(),
      this.fetchEntitlementsMetadata(),
    ]);
  };

  public initialize = async () => {
    await this.refreshAll();
  };

  public saveChanges = async (args: SaveChangesArgs) => {
    if (this.isDisposed()) {
      return;
    }

    const { feedDifference, approvals } = args;

    await putGroupFeedsPermissions({
      groupId: this.data.groupId,
      permissionId: this.data.permissionId,
      serviceId: this.data.serviceId,
      approvals,
      ...feedDifference,
    });

    await this.refreshAll();
  };

  public saveApproval = async ({ approvalId, payload }: { approvalId?: string; payload: Record<string, unknown> }) => {
    if (this.isDisposed()) {
      return;
    }

    if (approvalId) {
      await updatePermissionApproval(approvalId, payload);
    } else {
      await putPermissionApproval(payload);
    }

    await this.fetchApprovals();
  };

  public saveMetadata = async (entitlementId: string, payload: Record<string, unknown>) => {
    if (this.isDisposed()) {
      return;
    }

    await putPermissionMetadata(entitlementId, payload);
    await this.fetchEntitlementsMetadata();
  };

  public deleteMetadata = async (entitlementId: string) => {
    if (this.isDisposed()) {
      return;
    }

    await deletePermissionMetadata(entitlementId);
    await this.fetchEntitlementsMetadata();
  };
}

export function createEntitlementsContext(data: EntitlementsParams) {
  const model = new EntitlementsModel(data);
  const controller = new EntitlementsController(model);

  return {
    data,
    model,
    controller,
  };
}

export type EntitlementsContext = ReturnType<typeof createEntitlementsContext>;
